import React, { useEffect, useMemo } from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useOutletContext } from 'react-router-dom';
import palette from '../../../../theme/palette';
import { getReportsCollection } from './reportsCollectionObject';
import { useEffectiveSettings, SETTING } from '../../../../hooks/use-effective-settings';
import { CustomerTypes } from '../../../../types';

interface ContextType {
    setReportTitle: (title: string) => void;
    setReportDescText: (desc: string) => void;
}

// Shape returned by getReportsCollection (grouped report tiles).
interface ReportItem {
    icon: React.ReactNode;
    title: string;
    description: string;
    navigateUrl: string;
}
interface ReportGroup {
    groupTitle: string;
    reports: ReportItem[];
}
interface FlatReport extends ReportItem {
    group: string;
}

const BRAND = palette.darkBlue;

export const ListReports: React.FC = () => {
    const { setReportTitle, setReportDescText } = useOutletContext<ContextType>();
    useEffect(() => {
        setReportTitle('Reports');
        setReportDescText('Generate and review safety, risk, and activity reports.');
    }, [setReportTitle, setReportDescText]);

    const { getSetting } = useEffectiveSettings();
    const isSafetyReportEnabled = getSetting(SETTING.isSafetyReportEnabled, false);
    const isActivityReportEnabled = getSetting(SETTING.isActivityReportEnabled, false);
    const showRiskAssessment = getSetting(SETTING.showRiskAssessment, false);
    const showGroupsTeamsDashboard = getSetting(SETTING.showGroupsTeamsDashboard, false);
    const navigate = useNavigate();

    const reports: FlatReport[] = useMemo(() => {
        const collection = (getReportsCollection({
            isSafetyReportEnabled,
            isActivityReportEnabled,
            showRiskAssessment,
            showGroupsTeamsDashboard,
        } as CustomerTypes.Settings) ?? []) as ReportGroup[];
        return collection.flatMap((g) =>
            (g.reports ?? []).map((r) => ({ ...r, group: g.groupTitle })),
        );
    }, [isSafetyReportEnabled, isActivityReportEnabled, showRiskAssessment, showGroupsTeamsDashboard]);

    return (
        <Card sx={{ mt: 3 }}>
            {reports.map((report, i) => (
                <Stack
                    key={report.navigateUrl}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    onClick={() => navigate(report.navigateUrl)}
                    sx={{
                        cursor: 'pointer',
                        px: 3, py: 2,
                        borderTop: i === 0 ? 'none' : `1px solid ${palette.grey[300]}`,
                        transition: 'background-color .15s ease',
                        '& .chev': { transition: 'transform .15s ease, color .15s ease' },
                        '&:hover': {
                            bgcolor: palette.grey[100],
                            '& .chev': { color: BRAND, transform: 'translateX(3px)' },
                            '& .ttl': { color: BRAND },
                        },
                    }}
                >
                    {/* report icon (brand line icon, as used elsewhere in the app) */}
                    <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                        {report.icon}
                    </Box>

                    {/* title + description */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography className="ttl" sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}>
                            {report.title}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }} noWrap>
                            {report.description}
                        </Typography>
                    </Box>

                    {/* category, right-aligned so the column reads as a quick index */}
                    <Typography
                        sx={{
                            display: { xs: 'none', sm: 'block' },
                            fontSize: 11, fontWeight: 600, letterSpacing: '.06em',
                            textTransform: 'uppercase', color: 'text.disabled', flexShrink: 0,
                        }}
                    >
                        {report.group}
                    </Typography>

                    <ChevronRightIcon className="chev" sx={{ color: palette.grey[400], flexShrink: 0 }} />
                </Stack>
            ))}
        </Card>
    );
};
